import React from "react";
import { Link } from "react-router-dom";
import InfinoMediaLogo from "../../assets/img/clients/Infino Media.png";

// Adjust this path to wherever analytics.js actually lives
import { trackCaseStudyView, trackWhatsAppClick } from "../../utils/analytics";

const features = [
  {
    title: "Store Room Stock Management",
    text: "Maintain centralized stock records for materials available in the store room, including available quantities, stock movements and transaction history.",
  },
  {
    title: "Employee Material Requests",
    text: "Employees can raise material requests for the items required to complete their assigned work or operational activities.",
  },
  {
    title: "Material Issue Management",
    text: "Store users can review requests and issue required materials while maintaining a clear record of quantities issued against each request.",
  },
  {
    title: "Material Return",
    text: "Unused or remaining materials can be returned against the original request, keeping issued and returned quantities connected.",
  },
  {
    title: "Purchase Order Management",
    text: "Store users can create and manage purchase orders for materials required by the organization.",
  },
  {
    title: "Material Inward",
    text: "Record materials received from suppliers against purchase orders and maintain corresponding inward inventory transactions.",
  },
  {
    title: "Delivery Challan",
    text: "Generate and manage delivery challans for material movement while maintaining proper transaction documentation.",
  },
  {
    title: "CRI Management",
    text: "Create and maintain Customer Rejection Inward records as part of the organization's material and inventory workflow.",
  },
  {
    title: "CRO Management",
    text: "Manage Customer Rejection Outward transactions and maintain connected records for material movement.",
  },
  {
    title: "Transaction Receipts",
    text: "Generate transaction receipts for inventory movements and maintain a documented history of store transactions.",
  },
  {
    title: "Stock & Transaction Reports",
    text: "Generate reports covering stock availability, inward, outward, requests, returns and other inventory transactions.",
  },
  {
    title: "Connected Inventory Workflow",
    text: "Connect material requests, issues, returns, purchase orders, inward transactions and material movements through one centralized system.",
  },
];

const impact = [
  "Centralized store room stock management",
  "Better visibility of material availability",
  "Structured employee material request process",
  "Material issue tracking against employee requests",
  "Material return tracking against original requests",
  "Connected purchase order and material inward workflow",
  "Improved control over material movement",
  "Reduced dependency on manual inventory records",
  "Centralized transaction history",
  "Easy generation of delivery challans and receipts",
  "Centralized CRI and CRO management",
  "Simplified inventory and transaction reporting",
];

const screenshotTitles = {
  "Dashboard.png": "Inventory Dashboard",
  "Items.png": "Items & Stock Management",
  "Request.png": "Employee Material Request",
  "Return.png": "Material Return",
  "Purchase Orders.png": "Purchase Order Management",
  "Material Inward.png": "Material Inward",
  "Delivery Challans.png": "Delivery Challan",
  "Minimum Stock Report.png": "Minimum Stock Report",
  "Suppliers.png": "Supplier Management",
  "Customer Rejection Inward.png": "Customer Rejection Inward",
  "Customer Rejection Outward.png": "Customer Rejection Outward",
};

const inventoryImages = require.context(
  "../../assets/img/Inventory",
  false,
  /\.(png|jpe?g|jpeg|webp|gif)$/i
);

const screenshotData = inventoryImages.keys().map((file) => {
  const fileName = file.replace("./", "");

  return {
    fileName,
    image: inventoryImages(file),
  };
});

const tech = [
  "ASP.NET MVC",
  "SQL Server",
  "Entity Framework",
];

const workflow = [
  {
    number: "01",
    title: "Material Request",
    text: "An employee raises a material request for the items required from the store.",
  },
  {
    number: "02",
    title: "Material Issue",
    text: "The store verifies the request and issues the required material against it.",
  },
  {
    number: "03",
    title: "Material Return",
    text: "Unused or remaining material can be returned against the original request.",
  },
  {
    number: "04",
    title: "Purchase Order",
    text: "A purchase order can be created when additional material is required.",
  },
  {
    number: "05",
    title: "Material Inward",
    text: "Received material is recorded against the purchase order and reflected in inventory transactions.",
  },
  {
    number: "06",
    title: "Material Movement",
    text: "Delivery challans, CRI, CRO and transaction receipts document material movement.",
  },
];

const faqs = [
  {
    question: "What type of inventory system was developed for Infino Media?",
    answer:
      "HexxaTech Softwares developed a custom Inventory and Store Room Management System for Infino Media to manage store stock, employee material requests, material issue and return, purchase orders, material inward and inventory transactions.",
  },
  {
    question: "Can employees request materials through the system?",
    answer:
      "Yes. Employees can raise material requests for the items required for their assigned work or operational activities.",
  },
  {
    question: "Can issued materials be returned?",
    answer:
      "Yes. Unused or remaining material can be returned against the original material request, keeping the issue and return records connected.",
  },
  {
    question: "Does the system manage purchase orders and material inward?",
    answer:
      "Yes. The system supports purchase order management and material inward transactions, allowing received material to be recorded as part of the inventory workflow.",
  },
  {
    question: "What technologies were used?",
    answer:
      "The system was developed using ASP.NET MVC, SQL Server and Entity Framework.",
  },
];

export default function Inventory() {
  React.useEffect(() => {
    document.title =
      "Inventory & Store Management System for Infino Media | HexxaTech";

    // GA4: record a case-study-view event for this page
    trackCaseStudyView("Inventory - Infino Media");

    const metaTags = [
      {
        name: "description",
        content:
          "Case study of the custom Inventory & Store Management System developed by HexxaTech Softwares for Infino Media, covering store stock, employee material requests, material issue and return, purchase orders, material inward, delivery challans, CRI, CRO and inventory reports.",
      },
      {
        name: "keywords",
        content:
          "Infino Media inventory management system, Infino Media store management system, inventory management software, store room management software, material request software, material issue management, material return management, purchase order management, material inward system, delivery challan software, CRI management, CRO management, custom inventory software, ERP inventory system, HexxaTech Softwares",
      },
      {
        property: "og:title",
        content:
          "Inventory & Store Management System for Infino Media | HexxaTech Softwares",
      },
      {
        property: "og:description",
        content:
          "A custom Inventory and Store Room Management System developed by HexxaTech Softwares for Infino Media to manage material requests, stock, issues, returns, purchase orders, inward transactions and inventory documentation.",
      },
      {
        property: "og:url",
        content:
          "https://hexxatech.co.in/case-studies/inventory",
      },
      {
        property: "og:type",
        content: "article",
      },
      {
        property: "og:site_name",
        content: "HexxaTech Softwares",
      },
      {
        property: "og:image",
        content:
          "https://hexxatech.co.in/assets/inventory-case-study.jpg",
      },
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:title",
        content:
          "Inventory & Store Management System for Infino Media",
      },
      {
        name: "twitter:description",
        content:
          "Custom inventory and store management software developed by HexxaTech Softwares for Infino Media.",
      },
    ];

    metaTags.forEach(({ name, property, content }) => {
      const selector = name
        ? `meta[name="${name}"]`
        : `meta[property="${property}"]`;

      let meta = document.head.querySelector(selector);

      if (!meta) {
        meta = document.createElement("meta");

        if (name) {
          meta.setAttribute("name", name);
        } else {
          meta.setAttribute("property", property);
        }

        document.head.appendChild(meta);
      }

      meta.setAttribute("content", content);
    });

    const canonicalUrl =
      "https://hexxatech.co.in/case-studies/inventory";

    let canonical = document.head.querySelector(
      'link[rel="canonical"]'
    );

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", canonicalUrl);

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline:
        "Inventory & Store Management System for Infino Media",
      description:
        "Case study of a custom Inventory and Store Room Management System developed by HexxaTech Softwares for Infino Media.",
      url: canonicalUrl,
      author: {
        "@type": "Organization",
        name: "HexxaTech Softwares",
        url: "https://hexxatech.co.in",
      },
      publisher: {
        "@type": "Organization",
        name: "HexxaTech Softwares",
        url: "https://hexxatech.co.in",
      },
      about: {
        "@type": "SoftwareApplication",
        name: "Inventory & Store Management System",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
      },
      mentions: {
        "@type": "Organization",
        name: "Infino Media",
      },
    };

    let schemaScript = document.getElementById(
      "inventory-case-study-schema"
    );

    if (!schemaScript) {
      schemaScript = document.createElement("script");
      schemaScript.type = "application/ld+json";
      schemaScript.id = "inventory-case-study-schema";
      document.head.appendChild(schemaScript);
    }

    schemaScript.textContent = JSON.stringify(structuredData);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    let faqScript = document.getElementById(
      "inventory-faq-schema"
    );

    if (!faqScript) {
      faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.id = "inventory-faq-schema";
      document.head.appendChild(faqScript);
    }

    faqScript.textContent = JSON.stringify(faqSchema);

    return () => {
      const schema = document.getElementById(
        "inventory-case-study-schema"
      );

      const faqSchemaElement = document.getElementById(
        "inventory-faq-schema"
      );

      if (schema) schema.remove();

      if (faqSchemaElement) {
        faqSchemaElement.remove();
      }
    };
  }, []);

  return (
    <>
      <style>{`

        * {
          box-sizing: border-box;
        }

        .cs-page {
          min-height: 100vh;
          background:
            radial-gradient(
              circle at 10% 0%,
              rgba(37, 99, 235, 0.08),
              transparent 28%
            ),
            radial-gradient(
              circle at 90% 15%,
              rgba(99, 102, 241, 0.07),
              transparent 25%
            ),
            #ffffff;
          color: #111827;
          font-family:
            Inter,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        }

        .cs-container {
          max-width: 1240px;
          margin: 0 auto;
          padding: 60px 24px 80px;
        }

        .cs-breadcrumb {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .cs-breadcrumb:hover {
          color: #2563eb;
        }

        .cs-hero {
          max-width: 950px;
          margin: 45px auto 0;
          text-align: center;
        }

        .cs-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border: 1px solid rgba(37, 99, 235, 0.15);
          border-radius: 999px;
          background: rgba(37, 99, 235, 0.06);
          color: #2563eb;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .cs-eyebrow-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #2563eb;
        }

        .cs-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 30px auto 20px;
          min-height: 90px;
        }

        .cs-logo img {
          max-width: 260px;
          max-height: 100px;
          width: auto;
          height: auto;
          object-fit: contain;
          display: block;
        }

        .cs-client-label {
          margin-top: 8px;
          color: #64748b;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .cs-hero h1 {
          margin: 20px 0 14px;
          font-size: clamp(42px, 6vw, 64px);
          line-height: 1.05;
          letter-spacing: -0.045em;
          font-weight: 800;
          color: #0f172a;
        }

        .cs-gradient-text {
          background:
            linear-gradient(
              90deg,
              #2563eb,
              #4f46e5,
              #7c3aed
            );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .cs-subtitle {
          font-size: clamp(20px, 2.4vw, 26px);
          font-weight: 700;
          margin: 0 0 20px;
          color: #1e293b;
        }

        .cs-hero-description {
          max-width: 820px;
          margin: 0 auto;
          color: #64748b;
          font-size: 18px;
          line-height: 1.8;
        }

        .cs-trust {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 22px;
          margin-top: 28px;
          color: #94a3b8;
          font-size: 13px;
          flex-wrap: wrap;
        }

        .cs-trust-item {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .cs-trust-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #94a3b8;
        }

        .cs-project-meta {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-top: 45px;
        }

        .cs-meta-card {
          padding: 20px;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.8);
          text-align: left;
        }

        .cs-meta-label {
          display: block;
          margin-bottom: 7px;
          color: #94a3b8;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .cs-meta-value {
          color: #0f172a;
          font-size: 15px;
          font-weight: 700;
        }

        .cs-section-header {
          margin: 95px 0 34px;
          padding: 0 !important;
        }

        .cs-section-label {
          color: #2563eb;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .cs-section-header h2 {
          margin: 10px 0 0;
          font-size: clamp(28px, 3.6vw, 40px);
          letter-spacing: -0.04em;
          line-height: 1.15;
          color: #0f172a;
        }

        .cs-split {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 28px;
          padding-top: 10px;
        }

        .cs-panel {
          border: 1px solid #e2e8f0;
          border-radius: 24px;
          background: rgba(255, 255, 255, 0.85);
          box-shadow:
            0 20px 60px rgba(15, 23, 42, 0.06);
          padding: 36px;
          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
        }

        .cs-panel:hover {
          transform: translateY(-4px);
          box-shadow:
            0 25px 70px rgba(15, 23, 42, 0.09);
        }

        .cs-panel-label {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 14px;
        }

        .cs-panel p {
          color: #475569;
          line-height: 1.8;
          font-size: 15.5px;
          margin: 0;
        }

        .cs-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          padding-top: 15px;
        }

        .cs-feature-card {
          position: relative;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          background: #ffffff;
          padding: 28px;
          overflow: hidden;
          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease,
            border-color 0.3s ease;
        }

        .cs-feature-card::before {
          content: "";
          position: absolute;
          width: 120px;
          height: 120px;
          right: -70px;
          top: -70px;
          border-radius: 50%;
          background:
            radial-gradient(
              circle,
              rgba(37, 99, 235, 0.10),
              transparent 70%
            );
        }

        .cs-feature-card:hover {
          transform: translateY(-6px);
          border-color: #dbeafe;
          box-shadow:
            0 25px 60px rgba(15, 23, 42, 0.10);
        }

        .cs-feature-icon {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background:
            linear-gradient(
              135deg,
              rgba(37, 99, 235, 0.12),
              rgba(79, 70, 229, 0.08)
            );
          color: #2563eb;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 13px;
          margin-bottom: 18px;
        }

        .cs-feature-card h3 {
          margin: 0 0 8px;
          font-size: 16px;
          letter-spacing: -0.02em;
          color: #0f172a;
        }

        .cs-feature-card p {
          margin: 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.7;
        }

        .cs-workflow {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          padding-top: 15px;
        }

        .cs-workflow-card {
          position: relative;
          padding: 28px;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease;
        }

        .cs-workflow-card:hover {
          transform: translateY(-5px);
          box-shadow:
            0 20px 50px rgba(15, 23, 42, 0.08);
        }

        .cs-workflow-number {
          color: #2563eb;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.08em;
          margin-bottom: 14px;
        }

        .cs-workflow-card h3 {
          margin: 0 0 8px;
          font-size: 17px;
          color: #0f172a;
        }

        .cs-workflow-card p {
          margin: 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.7;
        }

        .cs-tech-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          padding-top: 15px;
        }

        .cs-tech-badge {
          border: 1px solid #e2e8f0;
          background: #ffffff;
          border-radius: 999px;
          padding: 10px 20px;
          font-size: 14px;
          font-weight: 600;
          color: #334155;
          box-shadow:
            0 8px 20px rgba(15, 23, 42, 0.04);
          transition:
            transform 0.2s ease,
            border-color 0.2s ease;
        }

        .cs-tech-badge:hover {
          transform: translateY(-2px);
          border-color: #bfdbfe;
        }

        .cs-impact-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          padding-top: 15px;
        }

        .cs-impact-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          padding: 20px 22px;
          background: #ffffff;
          transition:
            transform 0.25s ease,
            box-shadow 0.25s ease;
        }

        .cs-impact-item:hover {
          transform: translateY(-3px);
          box-shadow:
            0 15px 35px rgba(15, 23, 42, 0.06);
        }

        .cs-impact-check {
          flex-shrink: 0;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background:
            rgba(37, 99, 235, 0.1);
          color: #2563eb;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 800;
          margin-top: 2px;
        }

        .cs-impact-item span:last-child {
          color: #334155;
          font-size: 15px;
          line-height: 1.6;
        }

        .cs-screenshot-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-top: 15px;
        }

        .cs-screenshot-card {
          overflow: hidden;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          background: #ffffff;
          box-shadow:
            0 12px 35px rgba(15, 23, 42, 0.05);
          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease,
            border-color 0.3s ease;
        }

        .cs-screenshot-card:hover {
          transform: translateY(-6px);
          border-color: #dbeafe;
          box-shadow:
            0 25px 60px rgba(15, 23, 42, 0.10);
        }

        .cs-screenshot-preview {
          width: 100%;
          height: 230px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background:
            linear-gradient(
              145deg,
              #f8fafc,
              #ffffff
            );
          border-bottom: 1px solid #e2e8f0;
        }

        .cs-screenshot-preview img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
          transition:
            transform 0.4s ease;
        }

        .cs-screenshot-card:hover
        .cs-screenshot-preview img {
          transform: scale(1.03);
        }

        .cs-screenshot-info {
          padding: 18px 20px 20px;
        }

        .cs-screenshot-info h3 {
          margin: 0 0 6px;
          color: #0f172a;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        .cs-screenshot-info span {
          color: #94a3b8;
          font-size: 12px;
        }

        .cs-faq {
          display: grid;
          gap: 14px;
          padding-top: 15px;
        }

        .cs-faq-item {
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          background: #ffffff;
          padding: 24px;
        }

        .cs-faq-item h3 {
          margin: 0 0 9px;
          color: #0f172a;
          font-size: 16px;
        }

        .cs-faq-item p {
          margin: 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.7;
        }

        .cs-cta {
          position: relative;
          overflow: hidden;
          margin-top: 110px;
          padding: 70px 40px;
          border-radius: 30px;
          background:
            radial-gradient(
              circle at 10% 20%,
              rgba(96, 165, 250, 0.18),
              transparent 30%
            ),
            radial-gradient(
              circle at 90% 80%,
              rgba(129, 140, 248, 0.18),
              transparent 30%
            ),
            #0f172a;
          color: white;
          text-align: center;
        }

        .cs-cta::before {
          content: "";
          position: absolute;
          inset: 0;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: inherit;
          pointer-events: none;
        }

        .cs-cta-content {
          position: relative;
          max-width: 760px;
          margin: 0 auto;
        }

        .cs-cta h2 {
          margin: 0 0 16px;
          font-size: clamp(32px, 4vw, 48px);
          letter-spacing: -0.04em;
          line-height: 1.1;
        }

        .cs-cta p {
          margin: 0 auto 30px;
          max-width: 650px;
          color: #cbd5e1;
          line-height: 1.8;
          font-size: 16px;
        }

        .cs-cta-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 14px 24px;
          border-radius: 12px;
          background: white;
          color: #0f172a;
          text-decoration: none;
          font-weight: 700;
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
        }

        .cs-cta-button:hover {
          transform: translateY(-2px);
          box-shadow:
            0 12px 30px rgba(0, 0, 0, 0.25);
        }

        .cs-footer-credit {
          text-align: center;
          color: #94a3b8;
          font-size: 13px;
          margin-top: 40px;
        }

        @media (max-width: 900px) {

          .cs-container {
            padding-top: 50px;
          }

          .cs-split {
            grid-template-columns: 1fr;
          }

          .cs-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .cs-workflow {
            grid-template-columns: repeat(2, 1fr);
          }

          .cs-impact-grid {
            grid-template-columns: 1fr;
          }

          .cs-screenshot-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .cs-project-meta {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 700px) {

          .cs-container {
            padding-left: 18px;
            padding-right: 18px;
          }

          .cs-hero h1 {
            font-size: 38px;
          }

          .cs-hero-description {
            font-size: 16px;
          }

          .cs-grid {
            grid-template-columns: 1fr;
          }

          .cs-workflow {
            grid-template-columns: 1fr;
          }

          .cs-screenshot-grid {
            grid-template-columns: 1fr;
          }

          .cs-screenshot-preview {
            height: 240px;
          }

          .cs-panel {
            padding: 26px;
          }

          .cs-cta {
            padding: 50px 22px;
            border-radius: 22px;
          }

          .cs-trust {
            gap: 12px;
            flex-direction: column;
          }

          .cs-logo img {
            max-width: 200px;
            max-height: 80px;
          }
        }

      `}</style>

      <main className="cs-page">

        <div className="cs-container">

          {/* =====================================================
              BREADCRUMB
          ===================================================== */}

          <a
            href="/case-studies"
            className="cs-breadcrumb"
          >
            ← Back to Case Studies
          </a>

          {/* =====================================================
              HERO
          ===================================================== */}

          <section className="cs-hero">

            <div className="cs-eyebrow">
              <span className="cs-eyebrow-dot" />
              Client Case Study
            </div>

            <div className="cs-logo">
              <img
                src={InfinoMediaLogo}
                alt="Infino Media Logo"
              />
            </div>

            <div className="cs-client-label">
              Technology Solution Developed for Infino Media
            </div>

            <h1>
              <span className="cs-gradient-text">
                Inventory
              </span>
            </h1>

            <p className="cs-subtitle">
              Inventory & Store Room Management System
            </p>

            <p className="cs-hero-description">
              HexxaTech Softwares developed a custom Inventory and
              Store Room Management System for Infino Media to
              centralize material requests, stock management,
              material issue and return, purchase orders, material
              inward and inventory-related transactions.
            </p>

            <div className="cs-trust">

              <div className="cs-trust-item">
                <span className="cs-trust-dot" />
                Client: Infino Media
              </div>

              <div className="cs-trust-item">
                <span className="cs-trust-dot" />
                Custom Business Software
              </div>

              <div className="cs-trust-item">
                <span className="cs-trust-dot" />
                Inventory Management
              </div>

              <div className="cs-trust-item">
                <span className="cs-trust-dot" />
                Web-Based System
              </div>

            </div>

            <div className="cs-project-meta">

              <div className="cs-meta-card">

                <span className="cs-meta-label">
                  Client
                </span>

                <span className="cs-meta-value">
                  Infino Media
                </span>

              </div>

              <div className="cs-meta-card">

                <span className="cs-meta-label">
                  Solution
                </span>

                <span className="cs-meta-value">
                  Inventory & Store Management
                </span>

              </div>

              <div className="cs-meta-card">

                <span className="cs-meta-label">
                  Development Partner
                </span>

                <span className="cs-meta-value">
                  HexxaTech Softwares
                </span>

              </div>

            </div>

          </section>

          {/* =====================================================
              OVERVIEW
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Project Overview
            </div>

            <h2>
              A centralized inventory workflow built for Infino Media.
            </h2>

          </section>

          <section className="cs-split">

            <div className="cs-panel">

              <div
                className="cs-panel-label"
                style={{ color: "#2563eb" }}
              >
                Business Requirement
              </div>

              <p>
                Infino Media required a structured system to manage
                store room inventory and material transactions.
                The workflow needed to cover employee material
                requests, material issue, unused material returns,
                purchasing, inward material and supporting inventory
                documentation.
              </p>

            </div>

            <div className="cs-panel">

              <div
                className="cs-panel-label"
                style={{ color: "#7c3aed" }}
              >
                HexxaTech Solution
              </div>

              <p>
                HexxaTech Softwares developed a centralized inventory
                management platform that connects the different
                stages of material handling. Requests, issues,
                returns, purchase orders, inward transactions,
                delivery challans, CRI, CRO and transaction receipts
                can be managed within one connected workflow.
              </p>

            </div>

          </section>

          {/* =====================================================
              WORKFLOW
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Inventory Workflow
            </div>

            <h2>
              From employee request to complete material movement.
            </h2>

          </section>

          <section className="cs-workflow">

            {workflow.map((item) => (

              <div
                className="cs-workflow-card"
                key={item.number}
              >

                <div className="cs-workflow-number">
                  {item.number}
                </div>

                <h3>
                  {item.title}
                </h3>

                <p>
                  {item.text}
                </p>

              </div>

            ))}

          </section>

          {/* =====================================================
              FEATURES
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Key Features
            </div>

            <h2>
              Software capabilities developed for Infino Media.
            </h2>

          </section>

          <section className="cs-grid">

            {features.map((feature, index) => (

              <div
                className="cs-feature-card"
                key={feature.title}
              >

                <div className="cs-feature-icon">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>
                  {feature.title}
                </h3>

                <p>
                  {feature.text}
                </p>

              </div>

            ))}

          </section>

          {/* =====================================================
              TECHNOLOGY
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Technology
            </div>

            <h2>
              Technology used to build the solution.
            </h2>

          </section>

          <section className="cs-tech-row">

            {tech.map((technology) => (

              <span
                className="cs-tech-badge"
                key={technology}
              >
                {technology}
              </span>

            ))}

          </section>

          {/* =====================================================
              BUSINESS IMPACT
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Business Impact
            </div>

            <h2>
              Improving inventory visibility and control.
            </h2>

          </section>

          <section className="cs-impact-grid">

            {impact.map((item) => (

              <div
                className="cs-impact-item"
                key={item}
              >

                <span className="cs-impact-check">
                  ✓
                </span>

                <span>
                  {item}
                </span>

              </div>

            ))}

          </section>

          {/* =====================================================
              SCREENSHOTS
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              System Screenshots
            </div>

            <h2>
              Inside the Inventory Management System.
            </h2>

          </section>

          <section className="cs-screenshot-grid">

            {screenshotData.map((item) => {

              const title =
                screenshotTitles[item.fileName] ||
                item.fileName.replace(
                  /\.[^/.]+$/,
                  ""
                );

              return (
                <div
                  className="cs-screenshot-card"
                  key={item.fileName}
                >

                  <div className="cs-screenshot-preview">

                    <img
                      src={item.image}
                      alt={`${title} - Infino Media Inventory Management System developed by HexxaTech Softwares`}
                      loading="lazy"
                    />

                  </div>

                  <div className="cs-screenshot-info">

                    <h3>
                      {title}
                    </h3>

                    <span>
                      Infino Media Inventory Management System
                    </span>

                  </div>

                </div>
              );
            })}

          </section>

          {/* =====================================================
              FAQ
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Frequently Asked Questions
            </div>

            <h2>
              About the Infino Media inventory solution.
            </h2>

          </section>

          <section className="cs-faq">

            {faqs.map((faq) => (

              <article
                className="cs-faq-item"
                key={faq.question}
              >

                <h3>
                  {faq.question}
                </h3>

                <p>
                  {faq.answer}
                </p>

              </article>

            ))}

          </section>

          {/* =====================================================
              RELATED SERVICES
          ===================================================== */}

          <section className="cs-section-header">

            <div className="cs-section-label">
              Related Services
            </div>

            <h2>
              Services that support this inventory workflow.
            </h2>

          </section>

          <section className="cs-feature-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "42px" }}>
            <article className="cs-panel" style={{ padding: "22px" }}>
              <h3 style={{ marginTop: 0 }}>Inventory Management</h3>
              <p>Centralized stock, purchase and movement workflows for operational teams and reporting.</p>
              <Link to="/services/inventory-management" style={{ color: "#2563eb", fontWeight: 700 }}>Explore Inventory Management →</Link>
            </article>
            <article className="cs-panel" style={{ padding: "22px" }}>
              <h3 style={{ marginTop: 0 }}>ERP & Business Management</h3>
              <p>Connect purchases, sales, stock and operational reporting into a single business platform.</p>
              <Link to="/services/erp-business-management" style={{ color: "#2563eb", fontWeight: 700 }}>Explore ERP & Business Management →</Link>
            </article>
            <article className="cs-panel" style={{ padding: "22px" }}>
              <h3 style={{ marginTop: 0 }}>Custom Software Development</h3>
              <p>Build a system around your actual inventory, approval and reporting workflows.</p>
              <Link to="/services/custom-software-development" style={{ color: "#2563eb", fontWeight: 700 }}>Explore Custom Software Development →</Link>
            </article>
          </section>

          {/* =====================================================
              CTA
          ===================================================== */}

          <section className="cs-cta">

            <div className="cs-cta-content">

              <h2>
                Need a custom inventory
                <br />
                management system?
              </h2>

              <p>
                Like the solution developed for Infino Media?
                Tell HexxaTech Softwares about your inventory,
                store room and material movement requirements.
                We can design a custom software solution around
                your business workflow.
              </p>

               <a
                  href="https://api.whatsapp.com/send/?phone=919226591664&text=Hello+HexxaTech%2C+I+would+like+to+discuss+a+software+project.&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primary-button"
                  onClick={() => trackWhatsAppClick("inventory_case_study_cta")}
                >
                  Discuss Your Project
                  <span aria-hidden="true">→</span>
                </a>


            </div>

          </section>

          <p className="cs-footer-credit">
            Inventory Management System developed by
            {" "}
            <strong>
              HexxaTech Softwares
            </strong>
            {" "}
            for
            {" "}
            <strong>
              Infino Media
            </strong>
          </p>

        </div>

      </main>
    </>
  );
}
